import "pages/StandardTreatment/shared/masterData.scss";
import "pages/MasterPengobatanStandar/index.scss";
import React, { useEffect, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import Button from "components/atoms/Button";
import standardTreatmentService, { errorMessages } from "services/StandardTreatment";
import ClinicalConditionStep from "pages/MasterPengobatanStandar/components/ClinicalConditionStep";
import TherapyDoseStep from "pages/MasterPengobatanStandar/components/TherapyDoseStep";
import useServiceData from "../shared/useServiceData";
import useBootstrapModal from "../shared/useBootstrapModal";
import useRuleEditor from "../shared/useRuleEditor";
import useTherapyMasters from "../shared/useTherapyMasters";
import RuleFormModal, { RULE_MODAL_ID } from "../shared/RuleFormModal";
import { ErrorState, ReadOnlyBanner, VersionStatusPill, formatDateTime } from "../shared/ui";
import SaveStatus from "../shared/SaveStatus";

const RuleDetail = () => {
  const { id } = useParams();
  const history = useHistory();
  const ruleModal = useBootstrapModal(RULE_MODAL_ID);
  const masters = useTherapyMasters();
  const [readOnly, setReadOnly] = useState(true);
  const editor = useRuleEditor(id, { readOnly });
  const rule = editor.rule;
  const versionId = rule?.version_id;
  const version = useServiceData(
    () => (versionId ? standardTreatmentService.getVersion(versionId) : Promise.resolve(null)),
    [versionId]
  );
  // Hanya Versi DRAFT yang bisa disunting; selain itu hanya baca (409 dari backend).
  useEffect(() => setReadOnly(version.data?.status !== "DRAFT"), [version.data]);

  useEffect(() => {
    document.title = "SISMAL | Detail Rule";
  }, []);

  const rulesUrl = `/standard-treatment/rules?version_id=${versionId}`;

  const updateRule = (body) =>
    standardTreatmentService.updateRule(id, body).then(() => {
      ruleModal.close();
      toast.success("Rule diperbarui");
      editor.reload();
    });

  const deleteRule = () =>
    confirmAlert({
      title: `Hapus Rule ${rule.source_rule_code} #${rule.source_rule_seq}?`,
      message: "Rule beserta Kondisi dan Terapinya dihapus dari Draft ini.",
      buttons: [
        {
          label: "Ya, hapus",
          onClick: () =>
            standardTreatmentService
              .deleteRule(id)
              .then(() => {
                toast.success("Rule dihapus");
                history.push(rulesUrl);
              })
              .catch((err) => toast.error(errorMessages(err).join(" · "))),
        },
        { label: "Batal" },
      ],
    });

  const error = editor.error || version.error;
  if (error)
    return (
      <div className="master-data-page">
        <ErrorState messages={error} onRetry={() => Promise.all([editor.reload(), version.reload()])} />
      </div>
    );
  if (!rule || !version.data)
    return (
      <div className="master-data-page">
        <p className="mps-muted mb-0">Memuat rule...</p>
      </div>
    );

  return (
    <div className="master-data-page">
      <p className="mps-muted mb-1">
        <Link to="/standard-treatment/versions">Daftar Versi</Link> ›{" "}
        <Link to={rulesUrl}>Versi {version.data.version} · Rules</Link> › {rule.source_rule_code} #{rule.source_rule_seq}
      </p>
      <div className="action-wrapper">
        <div>
          <h1 className="page-title">{rule.name}</h1>
          <div className="d-flex flex-wrap align-items-center" style={{ gap: 8 }}>
            <VersionStatusPill status={version.data.status} />
            <span className={`mps-pill ${rule.is_active ? "is-success" : "is-neutral"}`}>
              Rule {rule.is_active ? "aktif" : "nonaktif"}
            </span>
            {!readOnly && <SaveStatus status={editor.saveStatus} errors={editor.saveErrors} />}
          </div>
        </div>
        {!readOnly && (
          <div className="action-buttons">
            <Button variant="secondary" size="normal" onClick={() => ruleModal.open("edit", rule)}>
              Edit informasi
            </Button>
            <Button variant="danger" size="normal" onClick={deleteRule}>
              Hapus rule
            </Button>
          </div>
        )}
      </div>

      {readOnly && (
        <div className="mt-3">
          <ReadOnlyBanner version={version.data} />
        </div>
      )}

      <h2 className="md-section-title">Informasi</h2>
      <dl className="mps-version-grid mt-3">
        <div>
          <dt>Kode rule</dt>
          <dd>{rule.source_rule_code}</dd>
        </div>
        <div>
          <dt>Split (seq)</dt>
          <dd>{rule.source_rule_seq}</dd>
        </div>
        <div>
          <dt>Prioritas</dt>
          <dd>{rule.priority}</dd>
        </div>
        <div>
          <dt>Diperbarui</dt>
          <dd>{formatDateTime(rule.updatedAt)}</dd>
        </div>
        <div className="mps-version-wide">
          <dt>Catatan klinis</dt>
          <dd>{rule.clinical_note || "—"}</dd>
        </div>
        <div className="mps-version-wide">
          <dt>Catatan perubahan</dt>
          <dd>{rule.change_note || "—"}</dd>
        </div>
      </dl>

      <h2 className="md-section-title">Kondisi Klinis</h2>
      <ClinicalConditionStep conditions={rule.rule_conditions} onChange={editor.setConditions} readOnly={readOnly} />

      <h2 className="md-section-title">Terapi & Dosis</h2>
      {masters.error ? (
        <ErrorState messages={masters.error} onRetry={masters.reload} />
      ) : (
        <div className="mt-3">
          <TherapyDoseStep therapies={rule.rule_therapies} onChange={editor.setTherapies} readOnly={readOnly} masters={masters} />
        </div>
      )}

      <RuleFormModal key={ruleModal.modal.openCount} mode="edit" rule={ruleModal.modal.item} onSubmit={updateRule} />
    </div>
  );
};

export default RuleDetail;
